import { useState } from 'react';
import { loadSession } from '../lib/api.js';

/**
 * Signing in.
 *
 * A six-digit code by email, the same flow the office uses, because a technician has no
 * password to remember and a password on a shared van phone is a password on a sun visor.
 *
 * This screen is reached rarely by design: the field session lasts 180 days. It exists for the
 * first day on a new phone and for the day after a device is revoked — and it is the one
 * screen in the app that genuinely needs the network, which it says.
 */
export function SignInScreen({ onSignedIn }: { onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const request = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/auth/request-code', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!response.ok) throw new Error('request_failed');
      setStage('code');
    } catch {
      setError('Impossible de demander un code. Vérifiez le signal.');
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch('/api/auth/verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      if (!response.ok) {
        setError(body?.message ?? 'Ce code ne correspond pas.');
        return;
      }
      await loadSession();
      onSignedIn();
    } catch {
      setError('Impossible de vérifier le code. Vérifiez le signal.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="signin">
      <h1>Mon Rendez-vous</h1>
      <p className="quiet">Terrain</p>

      {stage === 'email' ? (
        <>
          <label className="field">
            <span>Courriel</span>
            <input
              type="email"
              inputMode="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <button type="button" className="big big--primary" onClick={() => void request()} disabled={busy || !email.includes('@')}>
            Recevoir un code
          </button>
        </>
      ) : (
        <>
          <label className="field">
            <span>Code à six chiffres</span>
            <input
              className="tnum"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            />
          </label>
          <button type="button" className="big big--primary" onClick={() => void verify()} disabled={busy || code.length < 6}>
            Entrer
          </button>
          <button type="button" className="ghost" onClick={() => setStage('email')}>
            Changer de courriel
          </button>
        </>
      )}

      {error && (
        <p className="warn" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
